import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Table, TableBody, TableRow, TableCell, IconButton, Tooltip, ToggleButtonGroup, ToggleButton, Collapse } from "@mui/material";
import { ClassDetails, Student } from '@shared/types';
import AddClassIcon from '../../../assets/icons/iconAddClass.svg';
import IconImportTable from '../../../assets/SVGComponents/IconImportTable';
import IconEdit from '../../../assets/SVGComponents/iconEdit';
import UploadFileIcon from '../../../assets/icons/iconUpload.svg';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import { PersonAdd, Close as CloseIcon  } from '@mui/icons-material';
import MessageModal from './MessageModal';
import ExampleTable from '../../../assets/images/tableExample.png';

interface AddClassModalProps {
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
    handleAddClass: (classDetails: ClassDetails) => void;
}

export default function AddClassModal({ isModalOpen, setIsModalOpen, handleAddClass }: AddClassModalProps) {
    const { t } = useTranslation();
    const [newStudent, setNewStudent] = useState<Student>({ name: '', email: '' });
    const [students, setStudents] = useState<Student[]>([]);
    const [uploadedExcel, setUploadedExcel] = useState<File | null>(null);
    const [classDetails, setClassDetails] = useState<ClassDetails>({
        name: '',
        classId: null,
        students: []
    });
    const [isManualEntry, setIsManualEntry] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isExampleModalOpen, setIsExampleModalOpen] = useState(false);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleAddStudent = () => {
        if (
            newStudent.name &&
            newStudent.email &&
            validateEmail(newStudent.email)
        ) {
            setStudents([...students, newStudent]);
            setNewStudent({ name: "", email: "" });
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setUploadedExcel(file);
        }
    };

    const handleRemoveFile = () => {
        setUploadedExcel(null);
    };

    const handleSubmit = () => {
        if (!classDetails.name) {
            return;
        }

        try {
            if (isManualEntry) {
                handleAddClass({ ...classDetails, students });
            } else if (uploadedExcel) {
                handleAddClass({ ...classDetails, students: uploadedExcel });
            }
            setClassDetails({
                name: '',
                classId: null,
                students: []
            });
            setStudents([]);
            setUploadedExcel(null);
            setNewStudent({ name: '', email: '' });
            setIsModalOpen(false);
        } catch (error) {
            console.error("Error adding students to class:", error);
        }
    };

    const handleDragEnter = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragging(true);
        },
        []
    );

    const handleDragLeave = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragging(false);
        },
        []
    );

    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer.files[0];
        if (
            file &&
            (file.type === "application/vnd.ms-excel" ||
                file.type ===
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                file.type === "text/csv")
        ) {
            setUploadedExcel(file);
        }
    }, []);

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    return (
        <>
            <Dialog className="modal-add-class" open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <IconButton
                    onClick={() => setIsModalOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 12,
                        top: 12,
                        zIndex: 3,
                        color: 'white'
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogTitle>{t('newClass')}</DialogTitle>
                <img className="icon" src={AddClassIcon} alt="add-class-icon" />
                <DialogContent>
                    <h3 className="title">{t('giveNameToClass')}</h3>
                    <div className="fields-container">
                        <TextField
                            label={t('className')}
                            value={classDetails?.name}
                            onChange={(e) => setClassDetails({ ...classDetails, name: e.target.value })}
                            size="small"
                            sx={{
                                '& input': {
                                    textAlign: 'center'
                                }
                            }}
                        />
                    </div>
                    <Collapse in={classDetails.name.trim() !== ''}>
                        <h3 className="title" style={{marginBottom: '18px'}}>{t('addStudentsToClass')}</h3>
                        <div className="toggle-button-wrapper">
                            <ToggleButtonGroup
                                value={isManualEntry}
                                exclusive
                                size="small"
                                onChange={(_, value) => value !== null && setIsManualEntry(value)}
                                aria-label="entry mode"
                                className="toggle-button-group"
                            >
                                <Tooltip title={t('manualEntry')}>
                                    <ToggleButton value={true} aria-label="manual entry">
                                        <IconEdit color={isManualEntry ? "white" : "#e9dbf7"} />
                                    </ToggleButton>
                                </Tooltip>
                                <Tooltip title={t('uploadExcel')}>
                                    <ToggleButton value={false} aria-label="excel upload" >
                                        <IconImportTable color={!isManualEntry ? "white" : "#e9dbf7"} />
                                    </ToggleButton>
                                </Tooltip>
                            </ToggleButtonGroup>
                        </div>
                        <div className="student-container">
                            {isManualEntry ? (
                                <Table>
                                        <TableBody>
                                            {students.map((student, index) => (
                                                <TableRow key={index}>
                                                    <TableCell align="center" sx={{ width: '50px' }}>
                                                        {index + 1}
                                                    </TableCell>
                                                    <TableCell >{student.name}</TableCell>
                                                    <TableCell >{student.email}</TableCell>
                                                    <TableCell align="center" sx={{ width: '120px' }}>
                                                        <IconButton
                                                            onClick={() => {
                                                                setNewStudent(student);
                                                                setStudents(students.filter((_, i) => i !== index));
                                                            }}
                                                            size="small"
                                                        >
                                                            <Edit sx={{ color: '#B689E3' }} />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => setStudents(students.filter((_, i) => i !== index))}
                                                            size="small"
                                                        >
                                                            <Delete sx={{ color: '#B689E3' }} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell align="center">
                                                    {students.length + 1}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <TextField
                                                        label={t('studentName')}
                                                        value={newStudent.name}
                                                        onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                                                        fullWidth
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <TextField
                                                        label={t('studentEmail')}
                                                        value={newStudent.email}
                                                        onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                                                        fullWidth
                                                        size="small"
                                                        type="email"
                                                        error={newStudent.email !== '' && !validateEmail(newStudent.email)}
                                                        helperText={newStudent.email !== '' && !validateEmail(newStudent.email) ? t('invalidEmail') : ''}
                                                        autoComplete="email"
                                                        inputProps={{
                                                            name: 'email',
                                                            autoCapitalize: 'none'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <span>
                                                        <Tooltip title={t('addStudent')}>
                                                            <span>
                                                                <IconButton
                                                                    onClick={handleAddStudent}
                                                                    disabled={!newStudent.name || !newStudent.email || !validateEmail(newStudent.email)}
                                                                    className="add-student-button"
                                                                >
                                                                    <PersonAdd sx={{
                                                                        color: (!newStudent.name || !newStudent.email || !validateEmail(newStudent.email)) ? "#e9dbf7" : "#B689E3"
                                                                    }} />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                            ) : (
                                <div
                                    className={`upload-file-area ${isDragging ? 'dragging' : ''}`}
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                >
                                    <input
                                        type="file"
                                        hidden
                                        id="file-upload"
                                        onChange={handleFileUpload}
                                        accept=".xlsx,.xls,.csv"
                                    />
                                    <label htmlFor="file-upload" style={{ display: 'block', cursor: 'pointer', padding: '40px 40px 8px 40px', textAlign: 'center' }}>
                                        {uploadedExcel ? (
                                            <>
                                                <AttachFileIcon sx={{ fontSize: '48px', color: '#B689E3' }} />
                                                <p>{uploadedExcel.name}</p>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleRemoveFile();
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '8px',
                                                        insetInlineEnd: '8px',

                                                    }}
                                                >
                                                    <Delete sx={{
                                                        height: '32px',
                                                        width: '32px',
                                                        color: '#e9dbf7',
                                                        margin: '2px',
                                                        '&:hover': {
                                                            color: '#B689E3'
                                                        }
                                                    }} />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <img src={UploadFileIcon} alt="upload-file-icon" />
                                                <p>{t('dragAndDropOrClick')}</p>
                                                <Button
                                                    variant="text"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setIsExampleModalOpen(true);
                                                    }}
                                                >
                                                    {t('tableExamle')}
                                                </Button>
                                            </>
                                        )}
                                    </label>
                                </div>
                            )}
                        </div>
                    </Collapse>
                </DialogContent>
                <DialogActions >
                    <Button
                        className="cancel-button circle"
                        onClick={() => setIsModalOpen(false)}
                        variant="outlined"
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        className="add-class-button circle"
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={
                            !classDetails.name?.trim() ||
                            (isManualEntry
                                ? students.length === 0
                                : !uploadedExcel
                            )
                        }
                    >
                        {t('addClass')}
                    </Button>
                </DialogActions>
            </Dialog>

            <MessageModal
                isOpen={isExampleModalOpen}
                onClose={() => setIsExampleModalOpen(false)}
                title={t('excelStructure', 'מבנה קובץ האקסל')}
                type="info"
                message={
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ textAlign: 'center', fontWeight: '200' }}>
                            {t('uploadExcelInstructions')}
                            <br />
                            <ol style={{ paddingInlineStart: '20%', listStylePosition: 'inside', listStyleType: 'decimal', textAlign: 'justify' }}>
                                <li>{t('studentNameColumn')}</li>
                                <li>{t('studentEmailColumn')}</li>
                            </ol>
                        </p>
                        <img
                            src={ExampleTable}
                            alt={t('excelTableExample')}
                            style={{
                                maxWidth: '300px',
                                margin: '16px 0',
                                border: '3px solid #b689e333',
                                borderRadius: '12px'
                            }}
                        />
                    </div>
                }

            />
        </>
    );
}
